import { useRef, useState } from "react";
import { YStack, XStack, Text, View, Image } from "tamagui";
import {
  RiStore2Line,
  RiUserLine,
  RiFileTextLine,
  RiFireLine,
  RiMoneyDollarCircleLine,
  RiTruckLine,
  RiShieldCheckLine,
  RiCalendarCloseLine,
  RiQuillPenLine,
  RiCheckLine,
  RiFilePdf2Line,
  RiErrorWarningLine,
} from "react-icons/ri";
import RadiusCard from "@components/layout/RadiusCard";
import RadiusBoxLabel from "@components/layout/RadiusBoxLabel";
import appToast from "@components/Toast";
import { colors } from "@styles/colors";
import StepLayout from "../StepLayout";
import { PrimaryBtn } from "../Buttons";
import SignatureModal from "../SignatureModal";
import { mockSubmitContract } from "../mockData";
import type { CylinderStatus, Supplier } from "../types";

interface ContractStepProps {
  supplier: Supplier;
  isExisting?: boolean;
  onSigned: () => void;
  onBack: () => void;
}

const CONTRACT_POINTS = [
  { Icon: RiFireLine, color: colors.primary, title: "供應內容", desc: "桶裝瓦斯依需求配送(16/20/50KG)" },
  { Icon: RiMoneyDollarCircleLine, color: colors.green, title: "計價方式", desc: "依瓦斯行公告價格,訂購時即時呈現" },
  { Icon: RiTruckLine, color: colors.amber, title: "配送時間", desc: "預計 30-60 分鐘到府,以實際狀況為準" },
  { Icon: RiShieldCheckLine, color: colors.purple, title: "服務保障", desc: "若有問題可透過平台客服協助處理" },
  { Icon: RiCalendarCloseLine, color: "#EC4899", title: "解約方式", desc: "可隨時於平台中解除綁定,無綁約期" },
];

const CYLINDER_OPTIONS: { key: CylinderStatus; label: string; desc: string }[] = [
  { key: "own", label: "自備鋼瓶", desc: "我自己擁有合格鋼瓶" },
  { key: "other", label: "別家瓦斯桶", desc: "目前使用其他瓦斯行的鋼瓶" },
  { key: "none", label: "沒有鋼瓶", desc: "需要由瓦斯行提供" },
];

/** 步驟 4：線上簽約（契約重點 + 鋼瓶狀況 + 同意條款 + 親筆簽名）。簽署為 mock（待接後端）。 */
const ContractStep = ({ supplier, isExisting = false, onSigned, onBack }: ContractStepProps) => {
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [sigModalOpen, setSigModalOpen] = useState(false);
  const [sigDataUrl, setSigDataUrl] = useState<string | null>(null);
  const [hasCylinder, setHasCylinder] = useState<CylinderStatus | null>(null);
  const [signError, setSignError] = useState<string | null>(null);
  // demo：第一次簽署強制失敗以示範重簽流程（真實串接改由後端回應決定成敗）。
  const failedOnceRef = useRef(false);

  const hasSignature = !!sigDataUrl;
  const canSubmit = agreed && hasSignature && (isExisting || hasCylinder !== null) && !signing;

  const sign = async () => {
    if (!canSubmit) return;
    setSigning(true);
    setSignError(null);
    // TODO(api): 換成後端簽約 endpoint（送出 supplier、鋼瓶狀況、簽名 dataURL、phoneVerifiedTicket）。
    const res = await mockSubmitContract(!failedOnceRef.current);
    if (!res.ok) {
      // 簽名交易生成失敗 → 清除簽名要求重新簽署後再送出
      failedOnceRef.current = true;
      setSigning(false);
      setSigDataUrl(null);
      setSignError(`${res.reason || "簽署失敗"}，請重新簽署後再試`);
      appToast.error("簽署失敗，請重新簽署");
      return;
    }
    onSigned();
  };

  const Checklet = ({ done, label }: { done: boolean; label: string }) => (
    <XStack ai="center" gap="$2">
      <View width={18} height={18} borderRadius={9} background={done ? colors.green : colors.border} ai="center" jc="center">
        {done && <RiCheckLine size={11} color={colors.light} />}
      </View>
      <Text fontSize={12} color={done ? colors.green : colors.hint} fontWeight="500">
        {label}
      </Text>
    </XStack>
  );

  return (
    <YStack position="relative" height="100dvh" width="100vw">
      <StepLayout
        title="線上簽署契約"
        subtitle="完成簽署即可開始叫瓦斯"
        onBack={signing ? undefined : onBack}
        step={4}
        footer={
          <PrimaryBtn active={canSubmit} onPress={sign}>
            {signing ? "簽署中…" : "確認簽署並完成註冊"}
          </PrimaryBtn>
        }>
        <YStack gap={12}>
        {/* 契約雙方 */}
        <RadiusCard>
          <RadiusBoxLabel label="契約雙方" icon={<RiStore2Line color={colors.primary} />} />
          <YStack mt="$2">
            <XStack ai="center" gap="$3" py="$3" borderBottomWidth={1} borderBottomColor={colors.borderLight} borderStyle="dashed">
              <View width={40} height={40} borderRadius={10} ai="center" jc="center" className="primary-bg-gradient">
                <RiStore2Line size={20} color={colors.light} />
              </View>
              <YStack flex={1}>
                <Text fontSize={11} color={colors.hint} fontWeight="600">
                  供應方
                </Text>
                <Text fontSize={15} fontWeight="700" color={colors.grayDark}>
                  {supplier.company}
                </Text>
              </YStack>
            </XStack>
            <XStack ai="center" gap="$3" py="$3">
              <View width={40} height={40} borderRadius={10} ai="center" jc="center" background={colors.amber}>
                <RiUserLine size={20} color={colors.light} />
              </View>
              <YStack flex={1}>
                <Text fontSize={11} color={colors.hint} fontWeight="600">
                  需求方
                </Text>
                <Text fontSize={15} fontWeight="700" color={colors.grayDark}>
                  您
                </Text>
              </YStack>
            </XStack>
          </YStack>
        </RadiusCard>

        {/* 契約重點 */}
        <RadiusCard>
          <RadiusBoxLabel label="契約重點" icon={<RiFileTextLine color={colors.amber} />} theme="amber" />
          <YStack mt="$2">
            {CONTRACT_POINTS.map((item, i) => (
              <XStack
                key={item.title}
                gap="$3"
                py="$2.5"
                ai="flex-start"
                borderBottomWidth={i < CONTRACT_POINTS.length - 1 ? 1 : 0}
                borderBottomColor={colors.borderLight}
                borderStyle="dashed">
                <View mt="$0.5">
                  <item.Icon size={20} color={item.color} />
                </View>
                <YStack flex={1}>
                  <Text fontSize={13} fontWeight="700" color={colors.grayDark} mb="$0.5">
                    {item.title}
                  </Text>
                  <Text fontSize={12} color={colors.sub} lineHeight={18}>
                    {item.desc}
                  </Text>
                </YStack>
              </XStack>
            ))}
            <XStack
              mt="$2.5"
              p="$2.5"
              borderWidth={1.5}
              borderColor={colors.border}
              borderRadius={10}
              ai="center"
              jc="center"
              gap="$1.5"
              cursor="pointer">
              <RiFilePdf2Line size={16} color={colors.primary} />
              <Text fontSize={13} color={colors.primary} fontWeight="600">
                檢視完整契約條款
              </Text>
            </XStack>
          </YStack>
        </RadiusCard>

        {/* 鋼瓶狀況（僅新合作瓦斯行需要） */}
        {!isExisting && (
          <RadiusCard>
            <RadiusBoxLabel label="鋼瓶狀況" icon={<RiFireLine color={colors.primary} />} />
            <YStack mt="$2" gap="$2.5">
              <Text fontSize={13} color={colors.sub} fontWeight="600">
                請問您家中目前的瓦斯鋼瓶狀況?
              </Text>
              {CYLINDER_OPTIONS.map((opt) => {
                const active = hasCylinder === opt.key;
                return (
                  <XStack
                    key={opt.key}
                    p="$3"
                    borderRadius={12}
                    borderWidth={1.5}
                    borderColor={active ? colors.primary : colors.border}
                    background={active ? colors.primaryBg : colors.light}
                    ai="center"
                    gap="$3"
                    cursor="pointer"
                    onPress={() => setHasCylinder(opt.key)}>
                    <View
                      width={20}
                      height={20}
                      borderRadius={10}
                      borderWidth={2}
                      borderColor={active ? colors.primary : colors.gray200}
                      ai="center"
                      jc="center">
                      {active && <View width={10} height={10} borderRadius={5} background={colors.primary} />}
                    </View>
                    <YStack flex={1}>
                      <Text fontSize={14} fontWeight="700" color={active ? colors.primary : colors.grayDark}>
                        {opt.label}
                      </Text>
                      <Text fontSize={11} color={colors.hint}>
                        {opt.desc}
                      </Text>
                    </YStack>
                  </XStack>
                );
              })}
              <XStack background={colors.bg} borderWidth={1} borderColor={colors.border} borderRadius={10} p="$2.5" gap="$2" ai="flex-start">
                <Text fontSize={14}>💡</Text>
                <Text fontSize={11} color={colors.sub} flex={1} lineHeight={18}>
                  押金將於您實際下單時,依訂購的鋼瓶種類與數量計算,本頁不收取
                </Text>
              </XStack>
            </YStack>
          </RadiusCard>
        )}

        {/* 同意條款 */}
        <XStack
          background={colors.light}
          p="$3.5"
          borderRadius={12}
          borderWidth={1.5}
          borderColor={agreed ? "#BFDBFE" : colors.border}
          gap="$2.5"
          ai="flex-start"
          cursor={signing ? "default" : "pointer"}
          onPress={() => !signing && setAgreed(!agreed)}>
          <View width={22} height={22} borderRadius={6} borderWidth={2} borderColor={agreed ? colors.primary : colors.gray200} background={agreed ? colors.primary : colors.light} ai="center" jc="center" mt="$0.5">
            {agreed && <RiCheckLine size={14} color={colors.light} />}
          </View>
          <Text fontSize={13} color={colors.grayDark} flex={1} lineHeight={20}>
            我已閱讀並同意上述契約條款,以及平台的
            <Text color={colors.primary} fontWeight="600"> 服務條款 </Text>與
            <Text color={colors.primary} fontWeight="600"> 隱私政策</Text>
          </Text>
        </XStack>

        {/* 親筆簽名 */}
        <RadiusCard>
          <RadiusBoxLabel label="親筆簽名" icon={<RiQuillPenLine color={colors.amber} />} theme="amber" />
          <YStack mt="$2">
            {!hasSignature ? (
              <YStack
                py="$6"
                px="$3"
                borderRadius={12}
                borderWidth={2}
                borderColor="#FBBF24"
                borderStyle="dashed"
                background="#FFFDF7"
                ai="center"
                jc="center"
                gap="$2.5"
                cursor="pointer"
                onPress={() => setSigModalOpen(true)}>
                <RiQuillPenLine size={28} color={colors.amber} />
                <Text fontSize={15} fontWeight="700" color={colors.amberDark}>
                  點擊開啟簽名板
                </Text>
                <Text fontSize={12} color={colors.hint}>
                  將以全螢幕方式讓您簽名
                </Text>
              </YStack>
            ) : (
              <YStack>
                <View position="relative" background="#FFFDF7" borderWidth={2} borderColor="#BFDBFE" borderRadius={12} height={140} overflow="hidden" ai="center" jc="center">
                  <Image source={{ uri: sigDataUrl! }} width="90%" height="82%" resizeMode="contain" />
                  <XStack position="absolute" t={8} r={8} background={colors.bgGreen} py={3} px={8} borderRadius={20} ai="center" gap="$1">
                    <RiCheckLine size={12} color={colors.green} />
                    <Text fontSize={11} fontWeight="700" color={colors.greenText}>
                      已簽名
                    </Text>
                  </XStack>
                </View>
                <XStack
                  mt="$2.5"
                  p="$2.5"
                  borderRadius={10}
                  borderWidth={1.5}
                  borderColor={colors.border}
                  ai="center"
                  jc="center"
                  gap="$1.5"
                  cursor="pointer"
                  onPress={() => setSigModalOpen(true)}>
                  <RiQuillPenLine size={16} color={colors.amberDark} />
                  <Text fontSize={13} color={colors.amberDark} fontWeight="600">
                    重新簽名
                  </Text>
                </XStack>
              </YStack>
            )}
          </YStack>
        </RadiusCard>

        {/* 簽署失敗提示（需重新簽署） */}
        {signError && (
          <XStack background={colors.bgRed} borderWidth={1} borderColor={colors.dangerLight} borderRadius={12} p="$3" gap="$2" ai="flex-start">
            <View mt="$0.5">
              <RiErrorWarningLine size={18} color={colors.danger} />
            </View>
            <Text fontSize={12} color={colors.redText} flex={1} lineHeight={18}>
              {signError}
            </Text>
          </XStack>
        )}

        {/* 簽署條件 */}
        <RadiusCard>
          <Text fontSize={12} color={colors.hint} fontWeight="600" mb="$2.5">
            簽署條件
          </Text>
          <YStack gap="$2">
            {!isExisting && <Checklet done={hasCylinder !== null} label="確認鋼瓶狀況" />}
            <Checklet done={agreed} label="同意契約條款" />
            <Checklet done={hasSignature} label="完成親筆簽名" />
          </YStack>
        </RadiusCard>
        </YStack>
      </StepLayout>

      {/* 全螢幕簽名板（覆蓋整頁，置於 StepLayout 之外，避免被捲動容器裁切） */}
      <SignatureModal
        open={sigModalOpen}
        onClose={() => setSigModalOpen(false)}
        onConfirm={(url) => {
          setSigDataUrl(url);
          setSignError(null);
          setSigModalOpen(false);
        }}
      />
    </YStack>
  );
};

export default ContractStep;
